import React, { useMemo } from 'react';
import { SettingsPage } from '../../../contexts/settings-modal-context';
import useAuth from '../../../hooks/utils/useAuth';
import { openKeycloakChangePassword } from '../../../utils/keycloak-utils';

interface NavigationItem {
  text: SettingsPage;
  section?: string;
}

interface NavigationProps {
  selectedPage: SettingsPage | undefined;
  onPageSelect: (page: SettingsPage) => void;
  hasUnsavedChanges: boolean;
  onUnsavedChangesWarning: (nextPage: SettingsPage) => void;
}

export const Navigation: React.FC<NavigationProps> = ({
  selectedPage,
  onPageSelect,
  hasUnsavedChanges,
  onUnsavedChangesWarning,
}) => {
  const { user } = useAuth();

  const handlePageClick = (page: SettingsPage) => {
    if (page === SettingsPage.changePassword) {
      openKeycloakChangePassword();
      return;
    }

    if (hasUnsavedChanges) {
      onUnsavedChangesWarning(page);
    } else {
      onPageSelect(page);
    }
  };

  const accountSettingsItems: NavigationItem[] = useMemo(
    () => [
      {
        text: SettingsPage.myProfile,
      },
      {
        text: SettingsPage.notifications,
      },
      ...(user?.caller_role?.active_subscription?.package?.dependents_configuration?.enabled && !user.is_dependent
        ? [
            {
              text: SettingsPage.inviteDependents,
            } as NavigationItem,
          ]
        : []),
      {
        text: SettingsPage.changePassword,
      },
    ],
    [user],
  );

  const membershipItems: NavigationItem[] = useMemo(
    () => [
      {
        text: SettingsPage.myPlan,
      },
      ...(!user?.onUnlimitedPlan
        ? [
            {
              text: SettingsPage.paymentMethod,
            } as NavigationItem,
          ]
        : []),
      {
        text: SettingsPage.callHistory,
      },
    ],
    [user],
  );

  const infoItems: NavigationItem[] = [
    {
      text: SettingsPage.aboutUs,
    },
    {
      text: SettingsPage.privacyPolicy,
    },
    {
      text: SettingsPage.termsOfUse,
    },
  ];

  const renderMenuItem = (item: NavigationItem) => {
    const isSelected = selectedPage === item.text;
    return (
      <button
        key={item.text}
        onClick={() => handlePageClick(item.text)}
        className={`font-open-sans flex w-full items-center rounded-lg px-3 py-2.5 text-left text-base font-medium leading-6 transition-colors ${
          isSelected
            ? 'text-primary bg-button-secondary'
            : 'hover:text-primary text-section-header-slate hover:bg-button-secondary'
        }`}
      >
        <span>{item.text}</span>
      </button>
    );
  };

  return (
    <div className="flex h-full flex-col justify-between overflow-auto bg-white p-6">
      <div className="mb-10">
        <div className="mb-10 flex items-center justify-between">
          <p className="font-open-sans text-2xl font-bold leading-6 text-black">Settings</p>
        </div>
        <div className="space-y-6">
          <div>
            <p className="font-open-sans text-section-header-slate mb-3 text-sm font-semibold uppercase leading-6">
              Account Settings
            </p>
            <div className="space-y-1">{accountSettingsItems.map(renderMenuItem)}</div>
          </div>
          <div>
            <p className="font-open-sans text-section-header-slate mb-3 text-sm font-semibold uppercase leading-6">
              Membership Management
            </p>
            <div className="space-y-1">{membershipItems.map(renderMenuItem)}</div>
          </div>
        </div>
      </div>
      <div className="space-y-1">{infoItems.map(renderMenuItem)}</div>
    </div>
  );
};
